import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { ComposeEmailForm } from 'components/EmailSender/ComposeEmailForm';
import { Typography, colors } from '@leapeasy/ui-kit';

const ANIMATION_MS = 240;
const DEFAULT_SUBJECT = "You've been Invited to the Leap Portal";
const DEFAULT_CONTENT = 'You have been invited to the Leap Portal.';

export const InviteComposePanel = ({
  open,
  fromEmail,
  suggestedToEmail,
  selectedBuildings = [],
  loading,
  disableSend,
  onClose,
  onSend,
}) => {
  const [mounted, setMounted] = useState(open);
  const [isVisible, setIsVisible] = useState(open);
  const [toEmail, setToEmail] = useState('');
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setToEmail(suggestedToEmail || '');
      setSubject(DEFAULT_SUBJECT);
      setContent(DEFAULT_CONTENT);
      setTimeout(() => setIsVisible(true), 0);
      return;
    }

    if (mounted) {
      setIsVisible(false);
      const timer = setTimeout(() => setMounted(false), ANIMATION_MS);
      return () => clearTimeout(timer);
    }
  }, [open, mounted, suggestedToEmail]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(), ANIMATION_MS);
  };

  const handleSend = (latestContent) => {
    onSend({ toEmail, subject, content: latestContent });
  };

  if (!mounted) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 999,
        display: 'flex',
        justifyContent: 'flex-end',
        backgroundColor: isVisible ? 'rgba(60, 57, 61, 0.35)' : 'rgba(60, 57, 61, 0)',
        transition: `background-color ${ANIMATION_MS}ms ease`,
      }}
    >
      <Box
        sx={{
          width: '460px',
          height: '100%',
          overflow: 'auto',
          backgroundColor: 'white',
          padding: '24px',
          transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
          transition: `transform ${ANIMATION_MS}ms ease`,
        }}
      >
        {selectedBuildings.length > 0 && (
          <Box
            sx={{
              mb: '20px',
              p: '12px',
              borderRadius: '8px',
              border: '1px solid #EAEAEA',
              backgroundColor: '#F8F4F9',
            }}
          >
            <Typography
              variant="body2"
              style={{ fontWeight: 600, color: colors.purple[900], marginBottom: 8 }}
            >
              Properties in this invitation ({selectedBuildings.length})
            </Typography>
            <Box
              component="ul"
              sx={{
                m: 0,
                pl: 2.5,
                maxHeight: 160,
                overflowY: 'auto',
                color: colors.neutral[900],
              }}
            >
              {selectedBuildings.map((building) => (
                <Box component="li" key={building.building_id} sx={{ mb: 0.5 }}>
                  <Typography variant="body2" style={{ color: colors.neutral[900] }}>
                    {building.name || 'Unnamed property'}
                    {building.building_type ? ` · ${building.building_type}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <ComposeEmailForm
          title="New Invite"
          fromLabel="From"
          toLabel="Invitee"
          sendLabel="Send Invite"
          sendingLabel="Sending invite..."
          fromEmail={fromEmail}
          toEmail={toEmail}
          subject={subject}
          initialContent={content}
          onToEmailChange={setToEmail}
          onSubjectChange={setSubject}
          onClose={handleClose}
          onSend={handleSend}
          sendDisabled={!toEmail.trim() || disableSend || loading}
          loading={loading}
        />
      </Box>
    </Box>
  );
};
